import {
  CheckCircleOutlined,
  DeleteOutlined,
  ImportOutlined,
  MailOutlined,
  QrcodeOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  CustomTableColumn,
  Inventory,
  User,
  UserCustomAttributeType,
  UserStatus,
  UserTermination,
} from '@hefe/models';
import {
  BasketballUsersSlice,
  ModalsSlice,
  UsersSlice,
  currentUserSelector,
} from '@hefe/store';
import { clubHasSubscription, formatDate } from '@hefe/utils';
import { Button, Space, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { AiOutlineEye, AiOutlinePhone } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/store';
import CustomButton from '../../../core/components/button/custom-button.component';
import SubscriptionWrapper from '../../../core/components/subscription-wrapper/subscription-wrapper.component';
import CustomTable from '../../../core/components/table/custom-table.component';
import EditUserComponent from '../../components/edit-user/edit-user.component';
import UsersImportComponent from '../../components/users-import/users-import/users-import.component';
import ActivateUsersForm from '../../forms/activate-users/activate-users.form';
import AddEditUserForm from '../../forms/add-edit-user/add-edit-user.form';
import DeleteUsersForm from '../../forms/delete-users/delete-users.form';
import InviteUsersForm from '../../forms/invite-users/invite-users.form';
import SendWelcomeMailForm from '../../forms/send-welcome-mail/send-welcome-mail.form';

export default function UsersComponent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useSelector(currentUserSelector);
  const users = useSelector(BasketballUsersSlice.basketballUsersSelector);
  const customUserAttributes = useSelector(
    UsersSlice.usersCustomUserAttributesSelector
  );
  const loading = useSelector(
    BasketballUsersSlice.basketballUsersLoadingSelector
  );

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchCustomAttributes();
  }, []);

  const fetchUsers = () => {
    dispatch(BasketballUsersSlice.fetchBasketballUsers());
  };

  const fetchCustomAttributes = () => {
    if (clubHasSubscription(AuthSubscriptions.third, user)) {
      dispatch(UsersSlice.fetchAllCustomUserAttributes());
    }
  };

  const openUsersImporter = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <UsersImportComponent />,
        title: t('import_users'),
        hideOkButton: true,
        hideCancelButton: true,
        width: 1000,
      })
    );
  };

  const openInviteUsersModal = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <InviteUsersForm />,
        title: t('invite_users'),
        formId: 'invite-users',
      })
    );
  };

  const openDeleteUsersModal = () => {
    const selectedUsersObjects = users.filter((user) =>
      selectedUsers.includes(user.id)
    );

    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <DeleteUsersForm users={selectedUsersObjects} />,
        title: t('delete_users'),
        formId: 'delete-users',
      })
    );
  };

  const openActivateUsersModal = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <ActivateUsersForm userIds={selectedUsers} />,
        title: t('activate_users'),
        formId: 'activate-users',
      })
    );
  };

  const openSendWelcomeMailModal = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <SendWelcomeMailForm userIds={selectedUsers} />,
        title: t('send_welcome_mail'),
        formId: 'send-welcome-mail',
      })
    );
  };

  const openEditModal = (user: User) => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <EditUserComponent user={user} />,
        title: t('edit_user'),
        width: 1000,
        hideCancelButton: true,
        hideOkButton: true,
      })
    );
  };

  const getCustomAttributeColumns = () => {
    const columns: CustomTableColumn[] = [];

    customUserAttributes.forEach((attribute) => {
      const isDateAttribute = attribute.type === UserCustomAttributeType.DATE;
      columns.push({
        key: `custom-attribute-${attribute.id}`,
        title: attribute.name,
        default: false,
        // TODO implement filters/sorter
        //showFilter: !isDateAttribute,
        //showDateFilter: isDateAttribute,
        //onFilter: (value: any, record: any) => true,
        render: (user: User) => {
          let value = '';
          const customUserValue = user.custom_attribute_values?.find(
            (val) => val.attribute_id === attribute.id
          );
          if (customUserValue) {
            value = isDateAttribute
              ? formatDate(customUserValue.value)
              : customUserValue.value;
          }
          return value;
        },
      });
    });

    return columns;
  };

  const playerTeams = useMemo(() => {
    const teams = users.flatMap((user) => user.basketball_user?.player_teams);

    return teams?.filter(
      (team, index, self) => index === self.findIndex((t) => t?.id === team?.id)
    );
  }, [users]);

  const coachedTeams = useMemo(() => {
    const teams = users.flatMap((user) => user.basketball_user?.coached_teams);

    return teams?.filter(
      (team, index, self) => index === self.findIndex((t) => t?.id === team?.id)
    );
  }, [users]);

  const inventories = useMemo(() => {
    const inv = users.flatMap((user) => user?.inventories);

    return inv?.filter(
      (inventory, index, self) =>
        index === self.findIndex((o) => o?.id === inventory?.id)
    );
  }, [users]);

  const addUserBtnDisabledTooptip = useMemo(() => {
    const userLimit = user?.club_subscription?.limits.users;

    if (userLimit && users.length + 1 > userLimit) {
      return t('errors.permissions.users');
    }

    return undefined;
  }, [user?.club_subscription?.limits.users, users.length]);

  return (
    <CustomTable
      rowKey={'id'}
      loading={loading}
      dataSource={users}
      name={'basketball-users'}
      rowSelection
      onRowSelect={(selectedRows) => setSelectedUsers(selectedRows)}
      onRowClick={(item) => openEditModal(item)}
      addForm={{
        component: <AddEditUserForm />,
        title: t('add_user'),
        formId: 'add-edit-user-form-new',
        width: 1000,
        disabledTooltip: addUserBtnDisabledTooptip,
      }}
      columns={[
        {
          key: 'sex',
          title: t('sex.title'),
          dataIndex: 'sex',
          default: false,
          sortType: 'string',
          showFilter: true,
          render: (sex: number) => {
            switch (sex) {
              case 1:
                return 'm';
              case 2:
                return 'w';
              case 3:
                return 'd';
              default:
                return 'u';
            }
          },
        },
        {
          key: 'firstname',
          title: t('firstname'),
          default: true,
          dataIndex: 'firstname',
          sortType: 'string',
        },
        {
          key: 'lastname',
          title: t('lastname'),
          default: true,
          dataIndex: 'lastname',
          sortType: 'string',
        },
        {
          key: 'email',
          title: t('email'),
          default: true,
          dataIndex: 'email',
          sortType: 'string',
        },
        {
          key: 'birthday',
          title: t('birthday'),
          default: true,
          dataIndex: 'birthday',
          showDateFilter: true,
          sortType: 'date',
          render: (birthday: string) => {
            return birthday ? formatDate(birthday) : '';
          },
        },
        {
          key: 'address',
          title: t('address'),
          default: false,
          render: (item: User) => {
            return (
              <Space direction="vertical" size={0}>
                <Typography.Text>{item.street}</Typography.Text>
                <Typography.Text>
                  {item.zipcode} {item.city}
                </Typography.Text>
              </Space>
            );
          },
        },
        {
          key: 'contact',
          title: t('contact'),
          default: false,
          render: (item: User) => {
            return (
              <Space direction="vertical" size={0}>
                <Tooltip title={t('phone')}>
                  <Typography.Text>
                    <AiOutlinePhone size={10} /> {item.phone}
                  </Typography.Text>
                </Tooltip>
                {item.represented_by && item.represented_by !== '' && (
                  <Tooltip title={t('represented_by')}>
                    <Typography.Text>
                      <AiOutlineEye size={10} />
                      {item.represented_by}
                    </Typography.Text>
                  </Tooltip>
                )}
              </Space>
            );
          },
        },
        {
          key: 'status',
          title: t('status'),
          sortType: 'string',
          default: false,
          filters: [
            { text: t('active'), value: UserStatus.ACTIVE },
            { text: t('passive'), value: UserStatus.PASSIVE },
            { text: t('termination.terminated'), value: UserStatus.TERMINATED },
          ],
          onFilter: (value: any, user: User) => {
            return user.status === value;
          },
          render: (user: User) => {
            switch (user.status) {
              case UserStatus.ACTIVE:
                return t('active');
              case UserStatus.PASSIVE:
                return t('passive');
              case UserStatus.TERMINATED:
                return t('termination.terminated');
              default:
                return t('unknown');
            }
          },
        },
        {
          key: 'registration_status',
          title: t('registration_status'),
          default: false,
          sortType: 'string',
          filters: [
            { text: t('completed'), value: 'completed' },
            { text: t('in_progress'), value: 'in_progress' },
            { text: t('pending'), value: 'pending' },
          ],
          onFilter: (value: any, user: User) => {
            switch (value) {
              case 'completed':
                return !user.in_progress && !user.pending;
              case 'in_progress':
                return user.in_progress;
              case 'pending':
                return user.pending;
            }

            return false;
          },
          render: (user: User) => {
            if (user.in_progress) {
              return t('in_progress');
            } else if (user.pending) {
              return (
                <Tooltip title={t('information_texts.user_pending')}>
                  {t('pending')}
                </Tooltip>
              );
            }

            return t('completed');
          },
        },
        {
          key: 'termination',
          title: t('termination.singular'),
          dataIndex: 'active_termination',
          default: false,
          // Filter with boolean termination set or not
          filters: [
            { text: t('yes'), value: true },
            { text: t('no'), value: false },
          ],
          onFilter: (value: boolean, user: User) => {
            return value
              ? !!user.active_termination?.termination_date
              : !user.active_termination?.termination_date;
          },
          render: (activeTermination: UserTermination) => {
            if (activeTermination?.termination_date) {
              return formatDate(activeTermination.termination_date);
            }
            return '';
          },
        },
        {
          key: 'note',
          title: t('note'),
          dataIndex: 'note',
          default: false,
        },
        {
          key: 'tan',
          title: t('tan'),
          dataIndex: ['basketball_user', 'tan'],
          default: false,
          render: (tan: number) => (tan ? String(tan).padStart(3, '0') : '---'),
        },
        {
          key: 'roles',
          title: t('roles.role'),
          dataIndex: 'roles',
          default: false,
          filters: [
            { value: 'bb_admin', text: t('roles.bb_admin') },
            { value: 'bb_coach', text: t('roles.bb_coach') },
            { value: 'bb_referee', text: t('roles.bb_referee') },
          ],
          onFilter: (value: string, user: User) => {
            return user.role.includes(value);
          },
          render: (roles: string[]) =>
            roles.map((role) => t(`roles.${role}`)).join(', '),
        },
        {
          key: 'player_teams',
          title: `${t('teams')} (${t('player')})`,
          sortType: 'string',
          default: false,
          filters:
            playerTeams?.map((team) => ({
              text: team?.name ?? '',
              value: team?.id,
            })) ?? [],
          onFilter: (value: number, record: User) => {
            return record?.basketball_user?.player_teams
              ?.map((team) => team.id)
              .includes(value);
          },
          render: (user: User) => {
            return user.basketball_user?.player_teams
              ?.map((team) => team.name)
              .join(', ');
          },
        },
        {
          key: 'coached_teams',
          title: `${t('teams')} (${t('coach')})`,
          sortType: 'string',
          default: false,
          filters:
            coachedTeams?.map((team) => ({
              text: team?.name ?? '',
              value: team?.id,
            })) ?? [],
          onFilter: (value: number, record: User) => {
            return record?.basketball_user?.coached_teams
              ?.map((team) => team.id)
              .includes(value);
          },
          render: (user: User) => {
            return user.basketball_user?.coached_teams
              ?.map((team) => team.name)
              .join(', ');
          },
        },
        {
          key: 'inventories',
          dataIndex: 'inventories',
          title: `${t('inventory.plural')}`,
          sortType: 'string',
          default: false,
          filters:
            inventories?.map((inventory) => ({
              text: inventory?.name ?? '',
              value: inventory?.id,
            })) ?? [],
          onFilter: (value: number, record: User) => {
            return record?.inventories
              ?.map((inventory) => inventory.id)
              .includes(value);
          },
          render: (inventories: Inventory[]) => {
            return inventories?.map((inventory) => inventory.name).join(', ');
          },
        },
        /*
         *  add custom user attributes
         */
        ...getCustomAttributeColumns(),
      ]}
      toolbarItems={[
        <Button.Group>
          <SubscriptionWrapper
            subscription={AuthSubscriptions.first}
            title={t('invite_users')}
          >
            <CustomButton
              onClick={() => openInviteUsersModal()}
              icon={<MailOutlined />}
              tooltip={t('invite_users')}
            />
          </SubscriptionWrapper>
          <SubscriptionWrapper
            subscription={AuthSubscriptions.second}
            title={t('invite_users_link')}
          >
            <CustomButton
              onClick={() => navigate('/admin/users/invitation-links')}
              icon={<QrcodeOutlined />}
              tooltip={t('invite_users_link')}
            />
          </SubscriptionWrapper>
          <SubscriptionWrapper
            subscription={AuthSubscriptions.first}
            title={t('import_users')}
          >
            <CustomButton
              onClick={() => openUsersImporter()}
              icon={<ImportOutlined />}
              tooltip={t('import_users')}
            />
          </SubscriptionWrapper>
        </Button.Group>,
        <Button.Group>
          <CustomButton
            disabled={selectedUsers.length === 0}
            icon={<DeleteOutlined />}
            tooltip={t('delete_users')}
            onClick={() => openDeleteUsersModal()}
          />
          <CustomButton
            disabled={selectedUsers.length === 0}
            icon={<CheckCircleOutlined />}
            tooltip={t('activate_users')}
            onClick={() => openActivateUsersModal()}
          />
          <CustomButton
            disabled={selectedUsers.length === 0}
            icon={<SendOutlined />}
            tooltip={t('send_welcome_mail')}
            onClick={() => openSendWelcomeMailModal()}
          />
        </Button.Group>,
      ]}
    />
  );
}
